// Display helpers around breast-feed segments. Parsing lives in @shared/feed
// so web and api share one implementation.

import { parseSegments } from '@shared/feed.ts';
import type { BreastSegment, FeedingSession } from '@shared/types.ts';
import { fmtDur } from './format.ts';

export { parseSegments };

export type BreastSummary = {
  segments: BreastSegment[];
  leftSec: number;
  rightSec: number;
  totalActiveSec: number;
  pausedSec: number;
  /** True iff this feeding session has any meaningful per-side history. */
  hasHistory: boolean;
};

export function summarise(
  session: Pick<FeedingSession, 'type' | 'started_at' | 'ended_at' | 'segments' | 'is_paused'> & {
    is_paused?: 0 | 1 | number;
  },
  nowSec: number = Math.floor(Date.now() / 1000),
): BreastSummary {
  const segs = parseSegments(session.segments).slice().sort((a, b) => a.from - b.from);
  const isBreast = session.type === 'breast_left' || session.type === 'breast_right';

  // Synthesize the in-progress trailing segment if the session is still open
  // and not paused (so the timer keeps ticking on the current side).
  let live: BreastSegment[] = segs;
  if (isBreast && session.ended_at == null && !session.is_paused) {
    const boundary = segs.length ? segs[segs.length - 1].to : session.started_at;
    if (nowSec > boundary) {
      live = [
        ...segs,
        {
          side: session.type === 'breast_left' ? 'left' : 'right',
          from: boundary,
          to: nowSec,
        },
      ];
    }
  }

  let leftSec = 0;
  let rightSec = 0;
  for (const s of live) {
    const d = Math.max(0, s.to - s.from);
    if (s.side === 'left') leftSec += d;
    else rightSec += d;
  }
  const totalActiveSec = leftSec + rightSec;
  const end = session.ended_at ?? nowSec;
  const elapsed = Math.max(0, end - session.started_at);
  const pausedSec = Math.max(0, elapsed - totalActiveSec);

  return {
    segments: live,
    leftSec,
    rightSec,
    totalActiveSec,
    pausedSec,
    hasHistory: isBreast && live.length > 0,
  };
}

export function summaryLabel(s: BreastSummary, opts: { showPaused?: boolean } = {}): string {
  const parts: string[] = [];
  if (s.leftSec > 0) parts.push(`L ${fmtDur(Math.round(s.leftSec / 60))}`);
  if (s.rightSec > 0) parts.push(`R ${fmtDur(Math.round(s.rightSec / 60))}`);
  if (opts.showPaused && s.pausedSec >= 60) parts.push(`paused ${fmtDur(Math.round(s.pausedSec / 60))}`);
  return parts.join(' · ');
}
